import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Pages one uninterrupted stay at the end of the list may load. Reached when
 * appended pages are too short to push the sentinel back out of view, which would
 * otherwise walk the whole result set from a single paint. Scrolling the sentinel
 * off screen and back starts a fresh allowance.
 */
const MAX_PAGES_PER_CROSSING = 3;

/**
 * Observes a sentinel element at the end of a list and calls `onIntersect` while
 * it is in view. Returns a callback ref to attach to the sentinel, which mounts
 * and unmounts with the list rather than with this hook.
 *
 * One observer per mounted sentinel, kept across fetches: constructing an
 * observer against an element already inside the viewport re-reports it
 * immediately, so an observer rebuilt whenever a fetch settled would walk the
 * result set unprompted and — the query client doesn't retry — re-request a
 * failed page in a tight loop.
 *
 * Visibility is therefore state, not an event: a sentinel still on screen after a
 * page appends gets no second crossing to fire on, so `loadedCount` is what
 * continues the run. A page that lands asks for the next one, a page that fails to
 * land doesn't, and `MAX_PAGES_PER_CROSSING` bounds how far one stay can go.
 */
export const useInfiniteScrollSentinel = ({
  enabled,
  onIntersect,
  loadedCount,
}: {
  enabled: boolean;
  onIntersect: () => void;
  /**
   * Rows accumulated so far. Read only as a change signal — one fetch is issued
   * per distinct count, so a fetch that returns no new rows is not reissued.
   */
  loadedCount: number;
}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);

  const requestedAtCountRef = useRef<number | null>(null);
  const pagesThisCrossingRef = useRef(0);

  const ref = useCallback((element: HTMLDivElement | null) => {
    // jsdom has no IntersectionObserver, and neither do older Safari versions
    // than the browser support target.
    if (element === null || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsIntersecting(entry.isIntersecting),
      // Fires ahead of the viewport edge so the next page is requested before the
      // seller reaches the very bottom.
      { rootMargin: '200px' },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      setIsIntersecting(false);
    };
  }, []);

  // Fetching straight from the observer callback is what this hook exists to
  // avoid: the callback reports a crossing, and a page appended below a sentinel
  // that stayed in view produces no further crossing to hang the next fetch off.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!isIntersecting) {
      // The seller took the end of the list off screen, so their next arrival
      // earns a fresh allowance.
      pagesThisCrossingRef.current = 0;
      return;
    }
    if (!enabled) return;
    if (requestedAtCountRef.current === loadedCount) return;
    if (pagesThisCrossingRef.current >= MAX_PAGES_PER_CROSSING) return;

    requestedAtCountRef.current = loadedCount;
    pagesThisCrossingRef.current += 1;
    onIntersect();
  }, [isIntersecting, enabled, loadedCount, onIntersect]);

  return ref;
};
